import AddIcon from "@mui/icons-material/Add";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { alpha, styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState, type ReactNode } from "react";
import { DateCell } from "_shared/_views/tables/date-cells";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";

import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import RowLink from "_shared/_views/tables/row-link";
import {
  getOnboardingTemplateProblemCode,
  getOnboardingTemplateValidationErrors,
  onboardingTemplateManagementApi,
  readOnboardingTemplateProblemDetails,
  useCreateOnboardingTemplateDraftMutation,
  useCreateOnboardingTemplateMutation,
  useEditOnboardingTemplateDraftMutation,
  useGetOnboardingTemplateManagementDetailQuery,
  useGetOnboardingTemplateManagementListQuery,
  usePublishOnboardingTemplateDraftMutation,
  useReactivateOnboardingTemplateMutation,
  useRetireOnboardingTemplateMutation,
  useSetOnboardingTemplateDefaultMutation,
  useValidateOnboardingTemplateDraftMutation,
} from "work/onboarding-templates/_api/onboarding-template-management-api";
import type {
  OnboardingTemplateManagementDetail,
  OnboardingTemplateManagementListItem,
  OnboardingTemplateTerms,
  OnboardingTemplateValidationError,
  OnboardingTemplateVersionManagement,
} from "work/onboarding-templates/_schemas/onboarding-template-management-schema";
import OnboardingTemplateEditorDialog, {
  createEmptyOnboardingTemplateEditorValue,
  type OnboardingTemplateEditorValue,
} from "work/onboarding-templates/_views/onboarding-template-editor-dialog";
import OnboardingTemplateTermsView from "work/onboarding-templates/_views/onboarding-template-terms";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";

type EditorState = {
  mode: "create" | "edit";
  initialValue: OnboardingTemplateEditorValue;
};

type PreviewState = {
  displayName: string;
  terms: OnboardingTemplateTerms & { vendorDisplayName: string };
};

type TemplateRowState = "active" | "retired" | "unpublished";

const TemplateDetailsDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition",
})<{ open?: boolean }>(({ theme, open }) => ({
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    height: `calc(100% - 64px - ${shellTopBannersHeight()})`,
    overflowX: "hidden",
    top: `calc(64px + ${shellTopBannersHeight()})`,
    transition: theme.transitions.create("width", {
      easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
    width: open ? "440px" : 0,
  },
}));

const PageLayout = styled(Stack)(({ theme }) => ({
  minHeight: 0,
  overflow: "auto",
  padding: theme.spacing(1),
}));

const PageHeader = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
}));

const SelectableTemplateRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "templateState",
})<{ templateState: TemplateRowState }>(({ templateState, theme }) => {
  let color = theme.palette.warning.main;
  if (templateState === "retired") color = theme.palette.grey[900];
  if (templateState === "active") color = theme.palette.primary.main;

  return {
    backgroundColor: alpha(color, 0.1),
    cursor: "pointer",
    "&.MuiTableRow-hover:hover": {
      backgroundColor: alpha(color, 0.16),
    },
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: alpha(color, 0.2),
    },
  };
});

const TemplateName = styled(Typography)(() => ({
  fontWeight: 600,
}));

const TemplateSubtitle = styled(Typography)(() => ({
  display: "block",
}));

const InlineChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

const WrappingActions = styled(Stack)(() => ({
  flexWrap: "wrap",
}));

const DrawerContent = styled("aside")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: theme.spacing(2),
  width: "440px",
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  paddingBottom: theme.spacing(1),
}));

const DrawerBody = styled(Box)(() => ({
  minHeight: 0,
  overflowY: "auto",
}));

const VersionSectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const DraftDetails = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const DraftActions = styled(WrappingActions)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}));

const ValidationAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const VersionList = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const VersionCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5),
}));

const VersionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

const DetailList = styled("dl")(() => ({
  margin: 0,
}));

const DetailRow = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "minmax(110px, 2fr) minmax(0, 3fr)",
  padding: theme.spacing(0.75, 0),
  "&:first-of-type": {
    borderTop: 0,
    paddingTop: 0,
  },
}));

const DetailKey = styled("dt")(({ theme }) => ({
  ...theme.typography.body2,
  fontWeight: 600,
  margin: 0,
}));

const DetailValue = styled("dd")(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  margin: 0,
  minWidth: 0,
  overflowWrap: "anywhere",
}));

const EmptyVersions = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const PreviewChips = styled(WrappingActions)(() => ({}));

const CloneForm = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

export default function OnboardingTemplatesPage() {
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const catalogQuery = useGetOnboardingTemplateManagementListQuery({ status: "all" });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const detailQuery = useGetOnboardingTemplateManagementDetailQuery(
    selectedTemplateId ? { templateId: selectedTemplateId } : skipToken,
  );
  const [editor, setEditor] = useState<EditorState>();
  const [editorErrors, setEditorErrors] = useState<OnboardingTemplateValidationError[]>([]);
  const [editorIsStale, setEditorIsStale] = useState(false);
  const [preview, setPreview] = useState<PreviewState>();
  const [cloneTemplate, setCloneTemplate] = useState<OnboardingTemplateManagementDetail>();
  const [cloneSourceVersion, setCloneSourceVersion] = useState("");
  const [cloneNewVersion, setCloneNewVersion] = useState("");
  const [validationResult, setValidationResult] = useState<{
    templateId: string;
    version: string;
    errors: OnboardingTemplateValidationError[];
  }>();

  const [createTemplate, createState] = useCreateOnboardingTemplateMutation();
  const [createDraft, createDraftState] = useCreateOnboardingTemplateDraftMutation();
  const [editDraft, editState] = useEditOnboardingTemplateDraftMutation();
  const [validateDraft, validateState] = useValidateOnboardingTemplateDraftMutation();
  const [publishDraft, publishState] = usePublishOnboardingTemplateDraftMutation();
  const [setDefault, defaultState] = useSetOnboardingTemplateDefaultMutation();
  const [retireTemplate, retireState] = useRetireOnboardingTemplateMutation();
  const [reactivateTemplate, reactivateState] = useReactivateOnboardingTemplateMutation();
  const commandPending = createDraftState.isLoading || validateState.isLoading || publishState.isLoading ||
    defaultState.isLoading || retireState.isLoading || reactivateState.isLoading;

  function selectTemplate(templateId: string) {
    setSelectedTemplateId(templateId);
    setValidationResult(undefined);
  }

  function openTemplateDetails(templateId: string) {
    selectTemplate(templateId);
    setDetailsDrawerOpen(true);
  }

  function closeTemplateDetails() {
    setDetailsDrawerOpen(false);
    setSelectedTemplateId(undefined);
    setValidationResult(undefined);
  }

  function acceptCommandDetail(detail: OnboardingTemplateManagementDetail) {
    selectTemplate(detail.definition.templateId);
    dispatch(onboardingTemplateManagementApi.util.upsertQueryData(
      "getOnboardingTemplateManagementDetail",
      { templateId: detail.definition.templateId },
      detail,
    ));
  }

  function showCommandError(error: unknown, fallback: string) {
    const problem = readOnboardingTemplateProblemDetails(error);
    enqueueSnackbar(problem?.detail ?? fallback, { variant: "error" });
  }

  function openCreateEditor() {
    setValidationResult(undefined);
    setEditorErrors([]);
    setEditorIsStale(false);
    setEditor({ mode: "create", initialValue: createEmptyOnboardingTemplateEditorValue() });
  }

  async function openEditEditor(
    templateId: string,
    loadedDetail?: OnboardingTemplateManagementDetail,
    forceRefetch = false,
  ) {
    selectTemplate(templateId);
    let detail = loadedDetail;
    if (!detail && !forceRefetch && templateId === selectedTemplateId && detailQuery.data) {
      detail = detailQuery.data;
    }
    if (!detail) {
      try {
        detail = await dispatch(onboardingTemplateManagementApi.endpoints.getOnboardingTemplateManagementDetail.initiate(
          { templateId },
          { forceRefetch: true },
        )).unwrap();
      } catch (error) {
        showCommandError(error, "Could not load the onboarding template draft.");
        return;
      }
    }
    if (!detail.draft) return;
    setEditorErrors([]);
    setEditorIsStale(false);
    setEditor({
      mode: "edit",
      initialValue: {
        templateId: detail.definition.templateId,
        displayName: detail.definition.displayName,
        draft: toTerms(detail.draft),
      },
    });
  }

  async function saveEditor(value: OnboardingTemplateEditorValue) {
    const isCreating = editor?.mode === "create";
    const currentDraft = detailQuery.data?.draft;
    if (!isCreating && !currentDraft) {
      const error = new Error("The current draft is unavailable.");
      setEditorErrors(getOnboardingTemplateValidationErrors(error));
      setEditorIsStale(getOnboardingTemplateProblemCode(error) === "onboarding_template_stale_draft");
      showCommandError(error, "Could not save the onboarding template draft.");
      return;
    }

    try {
      if (isCreating) {
        const detail = await createTemplate({ request: value }).unwrap();
        acceptCommandDetail(detail);
        enqueueSnackbar("Created onboarding template draft", { variant: "success" });
      } else {
        const detail = await editDraft({
          templateId: value.templateId,
          version: value.draft.version,
          expectedRevision: currentDraft!.revision,
          request: value.draft,
        }).unwrap();
        acceptCommandDetail(detail);
        enqueueSnackbar("Saved onboarding template draft", { variant: "success" });
      }
      setEditor(undefined);
      setEditorErrors([]);
      setEditorIsStale(false);
    } catch (error) {
      setEditorErrors(getOnboardingTemplateValidationErrors(error));
      setEditorIsStale(getOnboardingTemplateProblemCode(error) === "onboarding_template_stale_draft");
      showCommandError(error, "Could not save the onboarding template draft.");
    }
  }

  async function clonePublishedVersion() {
    if (!cloneTemplate || !cloneSourceVersion || !cloneNewVersion.trim()) return;
    try {
      const detail = await createDraft({
        templateId: cloneTemplate.definition.templateId,
        request: { sourceVersion: cloneSourceVersion, newVersion: cloneNewVersion.trim() },
      }).unwrap();
      acceptCommandDetail(detail);
      setCloneTemplate(undefined);
      await openEditEditor(detail.definition.templateId, detail);
      enqueueSnackbar("Created a new version draft", { variant: "success" });
    } catch (error) {
      showCommandError(error, "Could not create a new version draft.");
    }
  }

  async function validateCurrentDraft(detail: OnboardingTemplateManagementDetail) {
    if (!detail.draft) return false;
    let result;
    try {
      result = await validateDraft({
        templateId: detail.definition.templateId,
        version: detail.draft.version,
      }).unwrap();
    } catch (error) {
      showCommandError(error, "Could not validate the draft.");
      return false;
    }
    setValidationResult({
      templateId: detail.definition.templateId,
      version: detail.draft.version,
      errors: result.errors,
    });
    enqueueSnackbar(result.isValid ? "Draft validation passed" : "Draft validation found problems", {
      variant: result.isValid ? "success" : "warning",
    });
    return result.isValid;
  }

  async function publishCurrentDraft(detail: OnboardingTemplateManagementDetail) {
    if (!detail.draft || !(await validateCurrentDraft(detail))) return;
    const response = await confirm({
      title: "Publish onboarding template version?",
      message: `Publish ${detail.definition.templateId} version ${detail.draft.version}? Published versions cannot change.`,
      okButtonText: "Publish immutable version",
      cancelButtonText: "Cancel",
    });
    if (response === ConfirmResponse.Cancel) return;
    try {
      const updated = await publishDraft({
        templateId: detail.definition.templateId,
        version: detail.draft.version,
        expectedRevision: detail.draft.revision,
      }).unwrap();
      acceptCommandDetail(updated);
      setValidationResult(undefined);
      enqueueSnackbar(`Published ${updated.definition.templateId} version ${updated.definition.currentPublishedVersion}`, {
        variant: "success",
      });
    } catch (error) {
      showCommandError(error, "Could not publish the draft.");
    }
  }

  async function publishTemplateDraft(templateId: string) {
    try {
      const detail = await dispatch(
        onboardingTemplateManagementApi.endpoints.getOnboardingTemplateManagementDetail.initiate(
          { templateId },
          { forceRefetch: true },
        ),
      ).unwrap();
      await publishCurrentDraft(detail);
    } catch (error) {
      showCommandError(error, "Could not load the onboarding template draft.");
    }
  }

  async function changeDefault(template: OnboardingTemplateManagementListItem) {
    const response = await confirm({
      title: "Change the default onboarding template?",
      message: `${template.displayName} will be selected first for new officer invitations.`,
      okButtonText: "Set as default",
      cancelButtonText: "Cancel",
    });
    if (response === ConfirmResponse.Cancel) return;
    try {
      await setDefault({ templateId: template.templateId }).unwrap();
      enqueueSnackbar(`${template.displayName} is now the default`, { variant: "success" });
    } catch (error) {
      showCommandError(error, "Could not change the default template.");
    }
  }

  async function changeStatus(template: OnboardingTemplateManagementListItem) {
    const retiring = template.status === "Active";
    const response = await confirm({
      title: `${retiring ? "Retire" : "Reactivate"} onboarding template?`,
      message: retiring
        ? `${template.displayName} will no longer be available for new invitations. Existing work will keep its captured terms.`
        : `${template.displayName} will be available for new invitations again. It will not become the default.`,
      okButtonText: retiring ? "Retire template" : "Reactivate template",
      cancelButtonText: "Cancel",
    });
    if (response === ConfirmResponse.Cancel) return;
    const changeStatusRequest = retiring
      ? retireTemplate({ templateId: template.templateId })
      : reactivateTemplate({ templateId: template.templateId });
    let detail;
    try {
      detail = await changeStatusRequest.unwrap();
    } catch (error) {
      showCommandError(error, `Could not ${retiring ? "retire" : "reactivate"} the template.`);
      return;
    }
    acceptCommandDetail(detail);
    enqueueSnackbar(`${template.displayName} was ${retiring ? "retired" : "reactivated"}`, { variant: "success" });
  }

  async function openCloneDialog(templateId: string) {
    selectTemplate(templateId);
    let detail;
    try {
      detail = await dispatch(onboardingTemplateManagementApi.endpoints.getOnboardingTemplateManagementDetail.initiate(
        { templateId },
        { forceRefetch: true },
      )).unwrap();
    } catch (error) {
      showCommandError(error, "Could not load template version history.");
      return;
    }
    setCloneTemplate(detail);
    setCloneSourceVersion(detail.definition.currentPublishedVersion ?? detail.versions[0]?.version ?? "");
    setCloneNewVersion("");
  }

  async function previewRow(template: OnboardingTemplateManagementListItem) {
    selectTemplate(template.templateId);
    let detail;
    try {
      detail = await dispatch(onboardingTemplateManagementApi.endpoints.getOnboardingTemplateManagementDetail.initiate(
        { templateId: template.templateId },
      )).unwrap();
    } catch (error) {
      showCommandError(error, "Could not load the template preview.");
      return;
    }
    const version = detail.versions.find((item) => item.version === detail.definition.currentPublishedVersion) ?? detail.draft;
    if (!version) return;
    setPreview({
      displayName: template.displayName,
      terms: { ...toTerms(version), vendorDisplayName: version.vendorDisplayName },
    });
  }

  const selectedDetail = detailQuery.data?.definition.templateId === selectedTemplateId
    ? detailQuery.data
    : undefined;
  const catalog = catalogQuery.data ?? [];

  return (
    <PageLayout spacing={2}>
      <PageHeader>
        <Box>
          <Typography component="h2" variant="h5">Onboarding Templates</Typography>
          <Typography color="text.secondary" variant="body2">
            Manage the exact terms used for new officer invitations. Published versions cannot change.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateEditor}>Create template</Button>
      </PageHeader>

      {catalogQuery.isLoading && <Typography aria-live="polite">Loading onboarding templates...</Typography>}
      {catalogQuery.isError && (
        <Alert severity="error" action={<Button color="inherit" onClick={() => void catalogQuery.refetch()}>Try Again</Button>}>
          Could not load onboarding templates.
        </Alert>
      )}
      {!catalogQuery.isLoading && !catalogQuery.isError && catalog.length === 0 && (
        <Alert severity="info">No onboarding templates exist. Create the first draft.</Alert>
      )}
      {catalog.length > 0 && (
        <TableContainer component={Paper}>
          <Table aria-label="Onboarding templates">
            <TableHead>
              <TableRow>
                <TableCell>Template</TableCell><TableCell>Published</TableCell><TableCell>Workflow</TableCell>
                <TableCell>Last update</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {catalog.map((template) => (
                <SelectableTemplateRow
                  key={template.templateId}
                  data-template-state={getTemplateRowState(template)}
                  hover
                  selected={detailsDrawerOpen && template.templateId === selectedTemplateId}
                  tabIndex={0}
                  templateState={getTemplateRowState(template)}
                  onClick={() => openTemplateDetails(template.templateId)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
                    event.preventDefault();
                    openTemplateDetails(template.templateId);
                  }}
                >
                  <TableCell>
                    <TemplateName>
                      <RowLink onClick={() => openTemplateDetails(template.templateId)}>
                        {template.displayName}
                      </RowLink>
                    </TemplateName>
                    {template.draftVersion && (
                      <TemplateSubtitle color="text.secondary" variant="caption">
                        Draft version {template.draftVersion}
                      </TemplateSubtitle>
                    )}
                    {template.status === "Retired" && (
                      <TemplateSubtitle color="text.secondary" variant="caption">Retired</TemplateSubtitle>
                    )}
                    {template.isDefault && <InlineChip label="Default" color="primary" size="small" />}
                  </TableCell>
                  <TableCell>{template.currentPublishedVersion ?? <NoValueDash />}</TableCell>
                  <TableCell>
                    {template.workflowVersion ? `v${template.workflowVersion} · ${formatParticipationMode(template.participationMode)}` : "Not published"}
                    {template.negotiable && <InlineChip label="Negotiable" size="small" />}
                  </TableCell>
                  <TableCell><DateCell value={template.updatedDate} /></TableCell>
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <WrappingActions direction="row" spacing={0.5} useFlexGap>
                      <Button size="small" onClick={() => openTemplateDetails(template.templateId)}>View history</Button>
                      {(template.currentPublishedVersion || template.draftVersion) && (
                        <Button size="small" onClick={() => void previewRow(template)}>Preview</Button>
                      )}
                      {template.currentPublishedVersion && !template.draftVersion && (
                        <Button size="small" onClick={() => void openCloneDialog(template.templateId)}>Create new version</Button>
                      )}
                      {template.draftVersion && (
                        <Button size="small" onClick={() => void openEditEditor(template.templateId)}>Edit draft</Button>
                      )}
                      {template.draftVersion && template.status === "Active" && (
                        <Button size="small" variant="contained" onClick={() => void publishTemplateDraft(template.templateId)}>
                          Publish
                        </Button>
                      )}
                      {!template.isDefault && template.status === "Active" && template.currentPublishedVersion && (
                        <Button size="small" onClick={() => void changeDefault(template)}>Set as default</Button>
                      )}
                      {!template.isDefault && template.status === "Active" && (
                        <Button size="small" color="error" onClick={() => void changeStatus(template)}>Retire</Button>
                      )}
                      {template.status === "Retired" && template.currentPublishedVersion && (
                        <Button size="small" onClick={() => void changeStatus(template)}>Reactivate</Button>
                      )}
                    </WrappingActions>
                  </TableCell>
                </SelectableTemplateRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TemplateDetailsDrawer
        open={detailsDrawerOpen}
        anchor="right"
        variant="persistent"
        closeAfterTransition={false}
      >
        <DrawerContent aria-label="Onboarding template details">
          <DrawerHeader>
            <Typography component="h3" variant="h5">
              {selectedDetail?.definition.displayName ?? "Template details"}
            </Typography>
            <Tooltip title="Minimize panel">
              <IconButton aria-label="Close template details" size="medium" onClick={closeTemplateDetails}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </DrawerHeader>
          {detailQuery.isFetching && selectedTemplateId && (
            <Typography aria-live="polite">Loading template details...</Typography>
          )}
          {detailQuery.isError && <Alert severity="error">Could not load the selected template details.</Alert>}
          {selectedDetail && (
            <DrawerBody>
              <VersionSectionTitle color="text.secondary" variant="subtitle2">Version history</VersionSectionTitle>
              {selectedDetail.draft && (
                <DraftDetails severity="info">
                  <VersionTitle>Draft</VersionTitle>
                  <VersionDetailRows version={selectedDetail.draft} />
                  <DraftActions direction="row" spacing={1} useFlexGap>
                    <Button size="small" onClick={() => void openEditEditor(selectedDetail.definition.templateId)}>Edit draft</Button>
                    <Button size="small" onClick={() => void validateCurrentDraft(selectedDetail)}>Validate draft</Button>
                    <Button size="small" variant="contained" onClick={() => void publishCurrentDraft(selectedDetail)}>Publish draft</Button>
                  </DraftActions>
                </DraftDetails>
              )}
              {validationResult &&
                validationResult.templateId === selectedDetail.definition.templateId &&
                validationResult.version === selectedDetail.draft?.version &&
                validationResult.errors.length > 0 && (
                <ValidationAlert severity="error">
                  <ul>{validationResult.errors.map((error) => <li key={`${error.path}-${error.message}`}>{error.path}: {error.message}</li>)}</ul>
                </ValidationAlert>
              )}
              {selectedDetail.versions.length === 0 ? (
                <EmptyVersions color="text.secondary">No published versions.</EmptyVersions>
              ) : (
                <VersionList spacing={1}>
                  {selectedDetail.versions.map((version) => (
                    <VersionCard key={version.version}>
                      <VersionTitle>Published</VersionTitle>
                      <VersionDetailRows version={version} />
                    </VersionCard>
                  ))}
                </VersionList>
              )}
            </DrawerBody>
          )}
        </DrawerContent>
      </TemplateDetailsDrawer>

      {editor && (
        <OnboardingTemplateEditorDialog
          open
          mode={editor.mode}
          initialValue={editor.initialValue}
          saving={createState.isLoading || editState.isLoading}
          staleDraft={editorIsStale}
          serverErrors={editorErrors}
          onClose={() => setEditor(undefined)}
          onPreview={(value) => setPreview({
            displayName: value.displayName,
            terms: {
              ...value.draft,
              vendorDisplayName: detailQuery.data?.draft?.vendorDisplayName ?? "AttorneyHub",
            },
          })}
          onReload={() => void openEditEditor(editor.initialValue.templateId, undefined, true)}
          onSubmit={saveEditor}
        />
      )}

      <Dialog open={Boolean(preview)} fullWidth maxWidth="md" onClose={() => setPreview(undefined)}>
        <DialogTitle>Exact officer terms preview</DialogTitle>
        {preview && (
          <DialogContent>
            <PreviewChips direction="row" spacing={1} useFlexGap>
              <Chip label={`Template ${preview.displayName}`} />
              <Chip label={`Version ${preview.terms.version}`} />
              <Chip label={formatParticipationMode(preview.terms.participationMode)} />
              <Chip label={`Workflow v${preview.terms.workflowVersion}`} />
              <Chip label={preview.terms.negotiable ? "Negotiable" : "Not negotiable"} />
            </PreviewChips>
            <OnboardingTemplateTermsView terms={preview.terms} />
          </DialogContent>
        )}
        <DialogActions><Button onClick={() => setPreview(undefined)}>Close preview</Button></DialogActions>
      </Dialog>

      {cloneTemplate && (
        <Dialog open onClose={() => setCloneTemplate(undefined)} fullWidth maxWidth="sm">
          <DialogTitle>Create a new template version</DialogTitle>
          <DialogContent>
            <CloneForm spacing={2}>
              <TextField select label="Source published version" value={cloneSourceVersion} onChange={(event) => setCloneSourceVersion(event.target.value)}>
                {cloneTemplate.versions.map((version) => <MenuItem key={version.version} value={version.version}>Version {version.version}</MenuItem>)}
              </TextField>
              <TextField label="New version" required value={cloneNewVersion} onChange={(event) => setCloneNewVersion(event.target.value)} />
            </CloneForm>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloneTemplate(undefined)}>Cancel</Button>
            <Button variant="contained" disabled={!cloneNewVersion.trim() || commandPending} onClick={() => void clonePublishedVersion()}>Create draft</Button>
          </DialogActions>
        </Dialog>
      )}

    </PageLayout>
  );
}

function VersionDetailRows({ version }: Readonly<{ version: OnboardingTemplateVersionManagement }>) {
  return (
    <DetailList>
      <VersionDetailRow label="Version" value={version.version} />
      <VersionDetailRow label="Revision" value={version.revision.toString()} />
      <VersionDetailRow label="Participation" value={formatParticipationMode(version.participationMode)} />
      <VersionDetailRow label="Workflow" value={`Version ${version.workflowVersion}`} />
      <VersionDetailRow label="Negotiable" value={formatBoolean(version.negotiable)} />
      <VersionDetailRow label="Conflicts waived" value={formatBoolean(version.conflictsCheckWaived)} />
      <VersionDetailRow label="Created" value={<DateCell value={version.createdDate} />} />
      {version.state === "Published" && (
        <VersionDetailRow
          label="Published"
          value={<DateCell value={version.publishedDate} />}
        />
      )}
    </DetailList>
  );
}

function VersionDetailRow({ label, value }: Readonly<{ label: string; value: ReactNode }>) {
  return (
    <DetailRow>
      <DetailKey>{label}</DetailKey>
      <DetailValue>{value}</DetailValue>
    </DetailRow>
  );
}

function formatBoolean(value: boolean): string {
  return value ? "Yes" : "No";
}

function getTemplateRowState(template: OnboardingTemplateManagementListItem): TemplateRowState {
  if (template.status === "Retired") return "retired";
  if (!template.currentPublishedVersion) return "unpublished";
  return "active";
}

function toTerms(version: OnboardingTemplateVersionManagement): OnboardingTemplateTerms {
  return {
    version: version.version,
    participationMode: version.participationMode,
    workflowVersion: version.workflowVersion,
    name: version.name,
    description: version.description,
    effectiveDatePolicy: version.effectiveDatePolicy,
    endDatePolicy: version.endDatePolicy,
    negotiable: version.negotiable,
    conflictsCheckWaived: version.conflictsCheckWaived,
    discount: version.discount,
    feeSchedule: version.feeSchedule,
    // Carried explicitly. This projection is the editor's whole view of a draft, so a field
    // missing here is a field the next save sends back empty.
    vendorTeam: version.vendorTeam,
    documents: version.documents,
  };
}

function formatParticipationMode(mode: string | null): string {
  if (mode === "SystemAssisted") return "System assisted";
  if (mode === "StaffReview") return "Staff review";
  return "Not published";
}
